import { FC, useEffect } from 'react';

import { TranslationKey, useTranslations } from '@lang';
import { Controller, useFormContext } from 'react-hook-form';

import { useIsDesktop } from '@common/hooks/useIsDesktop';

import { BlockSelectField } from '@common/components/FormElements/BlockSelectField';
import { SpaEntityFormValues } from '@common/components/SpaEntity/SpaEntityForm.schema';
import TimeSlotOptionHOC from './TimeSlotOptionHOC';

const TimeSlotOption = (props) => <TimeSlotOptionHOC parentType="Option" {...props} />;
const TimeSlotValue = (props) => <TimeSlotOptionHOC parentType="SingleValue" {...props} />;

export const TimeSlotSelector: FC<{
  arrivalTimesOptions: any[];
  disabled: boolean;
  labelTranslation?: TranslationKey;
  showPerPerson?: boolean;
}> = ({ arrivalTimesOptions, disabled, labelTranslation = 'spa.selectArrival', showPerPerson }) => {
  const { t, formatTime } = useTranslations();
  const isDesktop = useIsDesktop();
  const { control, setValue, getValues } = useFormContext<SpaEntityFormValues>();

  useEffect(() => {
    if (arrivalTimesOptions.length !== 1) {
      return;
    }
    const [onlyOption] = arrivalTimesOptions;
    if (getValues('arrivalTime')?.startDateTime === onlyOption.startDateTime) {
      return;
    }
    setValue('arrivalTime', onlyOption);
  }, [arrivalTimesOptions, getValues, setValue]);

  return arrivalTimesOptions ? (
    <Controller
      control={control}
      name="arrivalTime"
      render={({ field: { value, onChange } }) => (
        <BlockSelectField
          key={arrivalTimesOptions.map((o) => o.startDateTime).join(',')}
          components={{
            Option: (props) => TimeSlotOption({ ...props, showPerPerson }),
            SingleValue: (props) => TimeSlotValue({ ...props, showPerPerson }),
          }}
          {...({ getOptionValue: (option: any) => option.startDateTime } as any)}
          value={value ?? null}
          isSearchable={false}
          menuPlacement={isDesktop ? 'auto' : 'top'}
          label={t(labelTranslation)}
          isRequired
          options={arrivalTimesOptions}
          noOptionsMessage={() => t('treatment.noTimeslotsAvailable')}
          placeholder={t('product.selectPlaceholder')}
          useNativeDropdownOnMobile={true}
          renderMobileOption={(option: any) => formatTime(option.startDateTime)}
          onChange={onChange}
          id="time-slot-selector"
          isDisabled={disabled}
        />
      )}
    />
  ) : null;
};
